import { z } from 'zod';
import { PersonRef } from './people.js';
import { ProjectKind } from './projects.js';
/**
 * Invoicing, phase 1 (docs/adr/0054-invoicing-and-xero.md): Chapsi drafts invoices to an agency's clients from
 * unbilled time, fixed-price Jobs marked done and retainer fees, and syncs approved ones to Xero. This is not
 * the workspace's own Chapsi subscription (billing.ts, docs/adr/0044).
 *
 * Money is always whole cents (the smallest unit of the invoice's currency) as integers; quantities are hours
 * with at most two decimals. Tax rates are basis points (10% GST = 1000). Dates are calendar dates.
 */
export const CurrencyCode = z.string().regex(/^[A-Z]{3}$/, 'A three-letter currency code, like AUD');
/** A money amount in cents: at most ten million in the currency's main unit. */
export const Cents = z.number().int().min(0).max(1_000_000_000);
/** A line can be negative (a discount), so its unit amount can be too. */
export const SignedCents = z.number().int().min(-1_000_000_000).max(1_000_000_000);
export const TaxRateBp = z.number().int().min(0).max(10_000);
export const InvoiceStatus = z.enum(['draft', 'approved', 'sent', 'paid', 'void', 'credited']);
/** How Chapsi pushes an approved invoice to Xero: as a draft to review there, or ready to send (AUTHORISED). */
export const XeroPushStatus = z.enum(['DRAFT', 'AUTHORISED']);
export const RateScope = z.enum(['client', 'project', 'person']);
// --- Access ------------------------------------------------------------------------------------------
export const InvoicingLevel = z.enum(['none', 'status', 'manage']);
/**
 * What the signed-in person may do with invoicing (docs/adr/0054 "Privacy"). Owners manage; anyone else only what an
 * owner granted: `status` (invoice numbers, statuses and dates, never an amount) or `manage` (rates and amounts).
 */
export const InvoicingAccess = z.object({
    level: InvoicingLevel,
    canViewStatus: z.boolean(),
    canManage: z.boolean(),
    /** Connect or disconnect Xero: owners. */
    canConnect: z.boolean(),
    /** Choose who sees invoice status or manages invoices: owners. */
    canGrant: z.boolean(),
});
// --- Settings ----------------------------------------------------------------------------------------
export const InvoicingSettings = z.object({
    currency: CurrencyCode,
    defaultHourlyRateCents: Cents.nullable(),
    taxRateBp: TaxRateBp,
    taxLabel: z.string().min(1).max(20),
    paymentTermsDays: z.number().int().min(0).max(365),
    numberPrefix: z.string().max(12),
    numberDigits: z.number().int().min(1).max(10),
    /** The number the next approved invoice takes (skipping any already used). */
    nextNumber: z.number().int().min(1).max(999_999_999),
    defaultNotes: z.string().max(2000),
    xeroPushStatus: XeroPushStatus,
    xeroAccountCode: z.string().min(1).max(10),
    xeroTaxType: z.string().min(1).max(30),
    /** Push invoices Chapsi approves to Xero (off by default). */
    xeroSendInvoices: z.boolean(),
    /** Read invoices raised in Xero and show their status on projects (off by default). */
    xeroTrackInvoices: z.boolean(),
    /** Who besides the owners may see invoice status, or manage invoices. Owners see this list only. */
    access: z.array(z.object({ userId: z.uuid(), level: z.enum(['status', 'manage']) })).max(100),
    /** Chapi's nudge to the people who manage invoices ("3 invoices overdue"; docs/adr/0064). Off by default; owners choose. */
    overdueNudge: z.enum(['off', 'weekly', 'daily']),
});
export const UpdateInvoicingSettingsInput = z
    .object({
    currency: CurrencyCode,
    defaultHourlyRateCents: Cents.nullable(),
    taxRateBp: TaxRateBp,
    taxLabel: z.string().trim().min(1).max(20),
    paymentTermsDays: z.number().int().min(0).max(365),
    numberPrefix: z.string().trim().max(12).regex(/^[A-Za-z0-9/_-]*$/, 'Letters, digits, / _ and - only'),
    numberDigits: z.number().int().min(1).max(10),
    nextNumber: z.number().int().min(1).max(999_999_999),
    defaultNotes: z.string().max(2000),
    xeroPushStatus: XeroPushStatus,
    xeroAccountCode: z.string().trim().min(1).max(10).regex(/^[A-Za-z0-9.-]+$/),
    xeroTaxType: z.string().trim().min(1).max(30).regex(/^[A-Z0-9_]+$/, 'A Xero tax type, like OUTPUT'),
    xeroSendInvoices: z.boolean(),
    xeroTrackInvoices: z.boolean(),
    /** Owners only; anyone else sending a different list gets 404. Owners and guests can't be in it. */
    access: z.array(z.object({ userId: z.uuid(), level: z.enum(['status', 'manage']) })).max(100),
    /** Owners only, like `access`: anyone else sending a different value gets 404 (docs/adr/0064). */
    overdueNudge: z.enum(['off', 'weekly', 'daily']),
})
    .partial();
// --- Rates and prices -------------------------------------------------------------------------------
/**
 * One milestone of a fixed-price project's payment schedule (docs/adr/0054 stages, extended by docs/adr/0062): invoiced
 * once by default (several invoices only as part payments). Amounts and percentages are for people who manage
 * invoices; this shape is only ever sent to them.
 */
export const ProjectStage = z.object({
    id: z.uuid(),
    /** "M1", "M2": its place in the schedule, for invoice references ("SIM M2"). */
    code: z.string(),
    name: z.string(),
    amountCents: Cents,
    /** Its share of the project total in basis points (25% = 2500), for percentage schedules; null otherwise. */
    percentBp: z.number().int().nullable(),
    targetDate: z.iso.date().nullable(),
    /** Marked ready by hand (it also counts as ready when it has tasks and all are done). */
    ready: z.boolean(),
    /** The tasks that finish it. */
    taskIds: z.array(z.uuid()),
    /** The approved Chapsi invoice that billed it, if any. */
    invoiceNumber: z.string().nullable(),
    /** Has an invoice (from any source) that is not void: its amount can't change and it can't be removed. */
    invoiced: z.boolean(),
});
/** A payment schedule's milestones are fixed amounts, or percentages of the project total. */
export const ScheduleBasis = z.enum(['amount', 'percent']);
export const InvoicingRates = z.object({
    currency: CurrencyCode,
    defaultHourlyRateCents: Cents.nullable(),
    clients: z.array(z.object({
        clientId: z.uuid(),
        name: z.string(),
        /** The client's own currency, or null for the workspace's. */
        currencyOverride: CurrencyCode.nullable(),
        currency: CurrencyCode,
        hourlyRateCents: Cents.nullable(),
        xeroContactId: z.string().nullable(),
    })),
    projects: z.array(z.object({
        projectId: z.uuid(),
        clientId: z.uuid(),
        name: z.string(),
        key: z.string(),
        kind: ProjectKind,
        hourlyRateCents: Cents.nullable(),
        /** The monthly retainer fee, in the client's currency. */
        retainerFeeCents: Cents.nullable(),
        /** Hourly (time is billed at the rates) or a fixed price in stages (time is logged, not billed). */
        billingMode: z.enum(['hourly', 'fixed']),
        /** The fee the schedule splits (fixed price); null when the milestones are just added up. */
        totalCents: Cents.nullable(),
        scheduleBasis: ScheduleBasis,
        stages: z.array(ProjectStage),
    })),
    people: z.array(z.object({ person: PersonRef, hourlyRateCents: Cents.nullable() })),
});
const StageInput = z.object({
    id: z.uuid().optional(),
    name: z.string().trim().min(1).max(200),
    /** Amount schedules: required. Percentage schedules: worked out from the total and ignored if sent. */
    amountCents: Cents.optional(),
    /** Percentage schedules: basis points of the total (25% = 2500). */
    percentBp: z.number().int().min(1).max(10_000).nullable().optional(),
    ready: z.boolean(),
    targetDate: z.iso.date().nullable().optional(),
    /** "Done when these tasks are done": tasks of this project. */
    taskIds: z.array(z.uuid()).max(50).optional(),
});
/**
 * Hourly, or a fixed price paid by a schedule of milestones: fixed amounts (adding up to the total, when one is given)
 * or percentages of the total (adding up to 100%). One milestone at 100% is "the whole fee up front".
 */
export const SetProjectBillingInput = z
    .object({
    mode: z.enum(['hourly', 'fixed']),
    basis: ScheduleBasis.default('amount'),
    totalCents: Cents.nullable().optional(),
    /** The milestones in order. One already invoiced keeps its name and amount. */
    stages: z.array(StageInput).max(50).default([]),
})
    .superRefine((v, ctx) => {
    if (v.mode !== 'fixed')
        return;
    if (v.basis === 'percent') {
        if (v.totalCents === null || v.totalCents === undefined)
            ctx.addIssue({ code: 'custom', path: ['totalCents'], message: 'Set the project total to split it by percentages' });
        if (!v.stages.length)
            ctx.addIssue({ code: 'custom', path: ['stages'], message: 'Add at least one milestone' });
        else if (v.stages.some((st) => st.percentBp === null || st.percentBp === undefined))
            ctx.addIssue({ code: 'custom', path: ['stages'], message: 'Every milestone needs a percentage' });
        else {
            const sum = v.stages.reduce((n, st) => n + (st.percentBp ?? 0), 0);
            if (sum !== 10_000)
                ctx.addIssue({ code: 'custom', path: ['stages'], message: `The percentages add up to ${sum / 100}%, not 100%` });
        }
    }
    else if (v.stages.some((st) => st.amountCents === undefined)) {
        ctx.addIssue({ code: 'custom', path: ['stages'], message: 'Every milestone needs an amount' });
    }
    else if (v.totalCents !== null && v.totalCents !== undefined && v.stages.reduce((n, st) => n + (st.amountCents ?? 0), 0) !== v.totalCents) {
        ctx.addIssue({ code: 'custom', path: ['stages'], message: 'The milestones must add up to the project total' });
    }
});
export const SetRateInput = z.object({ scope: RateScope, id: z.uuid(), hourlyRateCents: Cents.nullable() });
/** A Job's fixed price, or a retainer project's monthly fee. Null removes it. */
export const SetPriceInput = z.object({ kind: z.enum(['job', 'retainer']), id: z.uuid(), amountCents: Cents.nullable() });
export const UpdateInvoicingClientInput = z.object({
    currency: CurrencyCode.nullable().optional(),
    /** A Xero ContactID to map this client to; null forgets the mapping (the next push finds or creates one). */
    xeroContactId: z.uuid().nullable().optional(),
});
// --- Unbilled work ----------------------------------------------------------------------------------
export const UnbilledQuery = z
    .object({ clientId: z.uuid(), from: z.iso.date(), to: z.iso.date() })
    .refine((q) => q.from <= q.to, { message: 'from must be on or before to', path: ['to'] });
export const UnbilledWork = z.object({
    clientId: z.uuid(),
    currency: CurrencyCode,
    from: z.iso.date(),
    to: z.iso.date(),
    time: z.object({
        entries: z.number().int(),
        minutes: z.number().int(),
        amountCents: z.number().int(),
        /** Minutes no rate applies to (they draft at zero until a rate is set). */
        minutesWithoutRate: z.number().int(),
        /** Minutes on retainer projects that have a monthly fee (left out unless asked for). */
        retainerProjectMinutes: z.number().int(),
        /** Minutes on fixed-price projects: logged, never billed by the hour. */
        fixedProjectMinutes: z.number().int(),
        /** Minutes marked non-billable (only while the workspace tracks billable time). */
        nonBillableMinutes: z.number().int(),
    }),
    /** Fixed-price stages marked ready and not billed yet. */
    stages: z.array(z.object({ stageId: z.uuid(), projectId: z.uuid(), projectName: z.string(), name: z.string(), amountCents: Cents })),
    jobs: z.array(z.object({ workItemId: z.uuid(), key: z.string(), title: z.string(), completedAt: z.string().nullable(), priceCents: Cents.nullable() })),
    retainers: z.array(z.object({ projectId: z.uuid(), name: z.string(), month: z.string(), feeCents: Cents })),
});
export const InvoiceGrouping = z.enum(['task', 'project', 'person', 'summary']);
export const CreateInvoiceDraftInput = z
    .object({
    clientId: z.uuid(),
    from: z.iso.date(),
    to: z.iso.date(),
    grouping: InvoiceGrouping.default('task'),
    includeTime: z.boolean().default(true),
    includeJobs: z.boolean().default(true),
    includeRetainers: z.boolean().default(true),
    includeStages: z.boolean().default(true),
    /** Also bill hours logged on retainer projects that have a monthly fee. */
    includeRetainerProjectTime: z.boolean().default(false),
})
    .refine((q) => q.from <= q.to, { message: 'from must be on or before to', path: ['to'] });
// --- Invoices ---------------------------------------------------------------------------------------
export const InvoiceLineKind = z.enum(['time', 'job', 'retainer', 'stage', 'manual']);
export const InvoiceLine = z.object({
    id: z.uuid(),
    kind: InvoiceLineKind,
    description: z.string(),
    quantity: z.number(),
    unitAmountCents: SignedCents,
    amountCents: z.number().int(),
    /** How many time entries, Jobs or retainer months this line bills. */
    sourceCount: z.number().int(),
});
export const InvoiceXero = z.object({
    invoiceId: z.string().nullable(),
    status: z.string().nullable(),
    syncedAt: z.string().nullable(),
    /** A short, safe reason the last sync failed ("Xero is not connected"); never a token or a response body. */
    error: z.string().nullable(),
});
export const InvoiceSummary = z.object({
    id: z.uuid(),
    number: z.string().nullable(),
    status: InvoiceStatus,
    clientId: z.uuid(),
    clientName: z.string(),
    currency: CurrencyCode,
    totalCents: z.number().int(),
    amountPaidCents: z.number().int(),
    issueDate: z.iso.date(),
    dueDate: z.iso.date(),
    xeroStatus: z.string().nullable(),
    createdAt: z.string(),
});
export const Invoice = InvoiceSummary.extend({
    periodFrom: z.iso.date().nullable(),
    periodTo: z.iso.date().nullable(),
    reference: z.string(),
    notes: z.string(),
    taxRateBp: TaxRateBp,
    taxLabel: z.string(),
    subtotalCents: z.number().int(),
    taxCents: z.number().int(),
    lines: z.array(InvoiceLine),
    approvedAt: z.string().nullable(),
    approvedBy: PersonRef.nullable(),
    sentAt: z.string().nullable(),
    paidAt: z.string().nullable(),
    voidedAt: z.string().nullable(),
    xero: InvoiceXero,
});
export const InvoiceList = z.object({ items: z.array(InvoiceSummary) });
export const InvoiceListQuery = z.object({ status: InvoiceStatus.optional(), clientId: z.uuid().optional() });
export const InvoiceLineInput = z.object({
    /** An existing line keeps what it bills; a line without an id is a manual line. */
    id: z.uuid().optional(),
    description: z.string().trim().min(1).max(1000),
    quantity: z.number().min(-100_000).max(100_000).refine((n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-6, 'At most two decimals'),
    unitAmountCents: SignedCents,
});
export const UpdateInvoiceInput = z.object({
    issueDate: z.iso.date().optional(),
    dueDate: z.iso.date().optional(),
    reference: z.string().trim().max(255).optional(),
    notes: z.string().max(4000).optional(),
    lines: z.array(InvoiceLineInput).max(500).optional(),
});
export const VoidInvoiceInput = z.object({
    /** For a paid invoice: confirm a credit note was raised in Xero. Its work goes back to unbilled. */
    creditNoteRaised: z.boolean().optional(),
});
// --- Invoice status on projects and tasks (no amounts without manage) --------------------------------------
/** What a project or task view shows: overdue is a sent, unpaid invoice past its due date; part-paid has a payment. */
export const InvoiceDisplayStatus = z.enum(['draft', 'not_sent', 'approved', 'sent', 'part_paid', 'paid', 'overdue', 'void', 'credited']);
export const InvoiceStatusQuery = z
    .object({ projectId: z.uuid().optional(), workItemId: z.uuid().optional() })
    .refine((q) => !!q.projectId !== !!q.workItemId, { message: 'projectId or workItemId' });
export const InvoiceStatusItem = z.object({
    /** A Chapsi invoice id, a Xero invoice id for one raised in Xero, or a recorded invoice's id. */
    id: z.string(),
    source: z.enum(['chapsi', 'xero', 'recorded']),
    number: z.string().nullable(),
    status: InvoiceDisplayStatus,
    issueDate: z.iso.date().nullable(),
    dueDate: z.iso.date().nullable(),
    /** Amounts only for people who manage invoices; null for everyone else. */
    currency: z.string().nullable(),
    totalCents: z.number().int().nullable(),
    amountDueCents: z.number().int().nullable(),
});
export const InvoiceStatusList = z.object({ items: z.array(InvoiceStatusItem), canSeeAmounts: z.boolean() });
export const XeroTrackedInvoice = z.object({
    xeroInvoiceId: z.uuid(),
    number: z.string(),
    reference: z.string(),
    contactName: z.string(),
    status: InvoiceDisplayStatus,
    xeroStatus: z.string(),
    issueDate: z.iso.date().nullable(),
    dueDate: z.iso.date().nullable(),
    currency: z.string(),
    totalCents: z.number().int(),
    amountDueCents: z.number().int(),
    clientId: z.uuid().nullable(),
    projectId: z.uuid().nullable(),
    match: z.enum(['reference', 'contact', 'manual']).nullable(),
});
export const XeroTrackedList = z.object({ items: z.array(XeroTrackedInvoice), syncedAt: z.string().nullable() });
export const XeroTrackedQuery = z.object({ unlinked: z.enum(['true', 'false']).optional() });
export const LinkXeroTrackedInput = z.object({ projectId: z.uuid().nullable() });
/** Overdue when sent, unpaid and past due (in the viewer's today); part-paid when something was paid. */
export function invoiceDisplayStatus(i, today) {
    if (i.status === 'paid')
        return 'paid';
    if (i.status === 'sent') {
        if (i.dueDate && i.dueDate < today)
            return 'overdue';
        return i.amountPaidCents > 0 ? 'part_paid' : 'sent';
    }
    return InvoiceDisplayStatus.options.includes(i.status) ? i.status : 'sent';
}
// --- Xero ------------------------------------------------------------------------------------------
export const XeroConnection = z.object({
    /** This server has Xero app credentials (XERO_CLIENT_ID / XERO_CLIENT_SECRET), or the e2e fake. */
    configured: z.boolean(),
    connected: z.boolean(),
    /** Xero refused the stored tokens: an owner or admin must connect again. */
    needsReconnect: z.boolean(),
    organisationId: z.string().nullable(),
    organisationName: z.string().nullable(),
    /** The organisations the connection can reach, when there is more than one to choose from. */
    organisations: z.array(z.object({ id: z.string(), name: z.string() })),
    connectedAt: z.string().nullable(),
    connectedBy: PersonRef.nullable(),
    lastError: z.string().nullable(),
    /** Xero granted writing invoices (needed to send them); a read-only connection must connect again to send. */
    canSend: z.boolean(),
});
export const XeroConnectStart = z.object({ url: z.url() });
export const XeroCallbackInput = z.object({ code: z.string().min(1).max(2000), state: z.string().min(20).max(200) });
export const ChooseXeroOrganisationInput = z.object({ organisationId: z.uuid() });
/** "INV-0042": the workspace's prefix and the number padded to its digits. */
export function formatInvoiceNumber(prefix, n, digits) {
    return `${prefix}${String(n).padStart(digits, '0')}`;
}
// --- Milestones, assignment and recorded invoices (docs/adr/0062-invoice-milestones.md) -----------------------
/**
 * Where an invoice comes from: drafted in Chapsi, tracked from an accounting system (Xero today; QuickBooks or MYOB
 * would be further sources behind the same adapter shape), or recorded by hand.
 */
export const InvoiceSource = z.enum(['chapsi', 'xero', 'recorded']);
export const MilestoneState = z.enum(['not_invoiced', 'ready', 'invoiced', 'part_invoiced', 'overdue', 'paid']);
/** An invoice as the pending view shows it. Amounts only for people who manage invoices. */
export const PendingInvoice = z.object({
    source: InvoiceSource,
    id: z.string(),
    number: z.string().nullable(),
    status: InvoiceDisplayStatus,
    issueDate: z.iso.date().nullable(),
    dueDate: z.iso.date().nullable(),
    /** Several invoices for one milestone are part payments. */
    partPayment: z.boolean(),
    currency: z.string().nullable(),
    /** Before tax, like the milestone's amount. */
    amountCents: z.number().int().nullable(),
});
export const PendingMilestone = z.object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    targetDate: z.iso.date().nullable(),
    state: MilestoneState,
    /** Ready to invoice because someone marked it, or because all its tasks are done. */
    readyBecause: z.enum(['marked', 'tasks']).nullable(),
    tasks: z.array(z.object({ id: z.uuid(), key: z.string(), title: z.string(), done: z.boolean() })),
    invoices: z.array(PendingInvoice),
    /** Money: null below "manage invoices", percentages included (they reveal the value). */
    amountCents: z.number().int().nullable(),
    percentBp: z.number().int().nullable(),
    /** Still to invoice (the amount less its live invoices), for manage only. */
    remainingCents: z.number().int().nullable(),
});
export const PendingProject = z.object({
    projectId: z.uuid(),
    name: z.string(),
    key: z.string(),
    clientId: z.uuid(),
    clientName: z.string(),
    currency: z.string().nullable(),
    totalCents: z.number().int().nullable(),
    milestones: z.array(PendingMilestone),
    /** Invoices assigned to the project but to no milestone. */
    otherInvoices: z.array(PendingInvoice),
});
const PendingBucket = z.object({ count: z.number().int(), amountCents: z.number().int().nullable() });
export const InvoicingPending = z.object({
    canSeeAmounts: z.boolean(),
    projects: z.array(PendingProject),
    /** Milestones by where they are; amounts (summed in each project's currency, so only when one currency) for manage. */
    summary: z.object({ notInvoiced: PendingBucket, ready: PendingBucket, unpaid: PendingBucket, overdue: PendingBucket, currency: z.string().nullable() }),
});
export const PendingQuery = z.object({ projectId: z.uuid().optional() });
/** An invoice to assign, with what it is assigned to and any suggested milestone. Manage only (it carries amounts). */
export const AssignableInvoice = z.object({
    source: InvoiceSource,
    id: z.string(),
    number: z.string().nullable(),
    contactName: z.string().nullable(),
    reference: z.string(),
    status: InvoiceDisplayStatus,
    issueDate: z.iso.date().nullable(),
    dueDate: z.iso.date().nullable(),
    currency: z.string(),
    totalCents: z.number().int().nullable(),
    projectId: z.uuid().nullable(),
    stageId: z.uuid().nullable(),
    workItemIds: z.array(z.uuid()),
    partPayment: z.boolean(),
    /** A milestone suggested from the reference ("SIM M2") or the amount, for a manager to confirm. Never applied alone. */
    suggestion: z.object({ projectId: z.uuid(), stageId: z.uuid(), reason: z.enum(['reference', 'amount']) }).nullable(),
});
export const AssignableInvoiceList = z.object({
    /** Invoices with no project, and invoices with a milestone suggestion waiting to be confirmed. */
    items: z.array(AssignableInvoice),
    syncedAt: z.string().nullable(),
});
/**
 * Assigns an invoice of any source to a project, optionally a milestone of it and tasks of it; projectId null removes
 * the assignment. A milestone that already has an invoice takes another only as a part payment.
 */
export const AssignInvoiceInput = z.object({
    source: InvoiceSource,
    invoiceId: z.uuid(),
    projectId: z.uuid().nullable(),
    stageId: z.uuid().nullable().optional(),
    workItemIds: z.array(z.uuid()).max(50).default([]),
    partPayment: z.boolean().default(false),
    /** The person confirmed the suggested milestone (recorded as such). */
    fromSuggestion: z.boolean().default(false),
});
export const DismissInvoiceSuggestionInput = z.object({ source: z.literal('xero'), invoiceId: z.uuid(), stageId: z.uuid() });
export const RecordedInvoiceStatus = z.enum(['not_sent', 'sent', 'part_paid', 'paid', 'void']);
const HttpsLink = z.url().max(2000).refine((u) => u.startsWith('https://'), 'Use an https:// link');
/** An invoice recorded by hand (no accounting integration). It never goes anywhere. Manage only. */
export const RecordedInvoice = z.object({
    id: z.uuid(),
    number: z.string(),
    issueDate: z.iso.date(),
    dueDate: z.iso.date().nullable(),
    status: RecordedInvoiceStatus,
    /** Overdue when sent or part-paid and past due in the workspace's today. */
    displayStatus: InvoiceDisplayStatus,
    currency: CurrencyCode,
    amountCents: Cents.nullable(),
    link: z.string().nullable(),
    projectId: z.uuid().nullable(),
    stageId: z.uuid().nullable(),
    workItemIds: z.array(z.uuid()),
    partPayment: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const RecordedInvoiceList = z.object({ items: z.array(RecordedInvoice) });
export const CreateRecordedInvoiceInput = z
    .object({
    number: z.string().trim().min(1).max(50),
    issueDate: z.iso.date(),
    dueDate: z.iso.date().nullable().optional(),
    status: RecordedInvoiceStatus.default('not_sent'),
    /** Defaults to the client's currency, else the workspace's. */
    currency: CurrencyCode.optional(),
    amountCents: Cents.nullable().optional(),
    link: HttpsLink.nullable().optional(),
    projectId: z.uuid().nullable().optional(),
    stageId: z.uuid().nullable().optional(),
    workItemIds: z.array(z.uuid()).max(50).default([]),
    partPayment: z.boolean().default(false),
})
    .refine((v) => !v.dueDate || v.dueDate >= v.issueDate, { message: 'The due date is before the issue date', path: ['dueDate'] });
export const UpdateRecordedInvoiceInput = z.object({
    number: z.string().trim().min(1).max(50).optional(),
    issueDate: z.iso.date().optional(),
    dueDate: z.iso.date().nullable().optional(),
    status: RecordedInvoiceStatus.optional(),
    currency: CurrencyCode.optional(),
    amountCents: Cents.nullable().optional(),
    link: HttpsLink.nullable().optional(),
});
/** A recorded invoice's status as projects show it: overdue when sent or part-paid and past due. */
export function recordedDisplayStatus(r, today) {
    if ((r.status === 'sent' || r.status === 'part_paid') && r.dueDate && r.dueDate < today)
        return 'overdue';
    return r.status;
}
//# sourceMappingURL=invoicing.js.map